<template>
	<div class="performance-layout">
		<PCard>
			<template #title>
				<div class="flex align-items-center justify-content-between flex-wrap gap-3">
					<div class="flex flex-column gap-1">
						<WTitle text="Performance (PPM)" />
						<WSubTitle text="Plant performance ratio — minute samples and hourly averages" />
					</div>
					<div class="flex align-items-center gap-2 flex-wrap">
						<PDropdown
							:options="periodOptions"
							optionLabel="label"
							optionValue="value"
							placeholder="Select billing period"
							style="min-width: 12rem;"
							v-model="billingPeriodId"
							@change="onPeriodChange()"
						/>
						<PButton
							v-if="canRun"
							:disabled="!billingPeriodId || running"
							:loading="running"
							@click="runPpm()"
							icon="pi pi-play"
							label="Run PPM"
							rounded
							size="small"
							severity="success"
							v-tooltip.left="'Recompute performance ratio for this period'"
						/>
						<PButton
							:disabled="!billingPeriodId || loading"
							@click="refresh()"
							icon="pi pi-refresh"
							label="Refresh"
							outlined
							rounded
							size="small"
						/>
					</div>
				</div>
			</template>

			<template #content>
				<div v-if="!billingPeriodId" class="performance-empty">
					<i class="mdi mdi-chart-line performance-empty-icon" />
					<p>Select a billing period to view PPM results.</p>
				</div>

				<template v-else>
					<!-- Summary -->
					<div class="summary-grid mb-4">
						<div class="summary-card">
							<div class="summary-label">Period</div>
							<div class="summary-value">
								{{ results?.period?.name || '—' }}
								<PTag
									v-if="results?.period"
									:severity="periodSeverity(results.period.status)"
									class="ml-2"
								>{{ results.period.status }}</PTag>
								<PTag
									v-if="results?.period?.is_trial"
									severity="warning"
									class="ml-1"
								>trial</PTag>
							</div>
						</div>
						<div class="summary-card">
							<div class="summary-label">Minute samples</div>
							<div class="summary-value">{{ summary.sample_count }}</div>
						</div>
						<div class="summary-card">
							<div class="summary-label">Valid hours</div>
							<div class="summary-value text-green-500">{{ summary.valid_hours }}</div>
						</div>
						<div class="summary-card">
							<div class="summary-label">Invalid hours</div>
							<div class="summary-value text-orange-500">{{ summary.invalid_hours }}</div>
						</div>
						<div class="summary-card">
							<div class="summary-label">Avg PR (valid hours)</div>
							<div class="summary-value">{{ formatPr(summary.avg_pr) }}</div>
						</div>
						<template v-if="hasTcMetrics">
							<div class="summary-card">
								<div class="summary-label">Avg APR (actual, temp-corrected)</div>
								<div class="summary-value">{{ formatPr(summary.avg_apr) }}</div>
							</div>
							<div class="summary-card">
								<div class="summary-label">Avg PPR (projected)</div>
								<div class="summary-value">{{ formatPr(summary.avg_ppr) }}</div>
							</div>
							<div class="summary-card">
								<div class="summary-label">Avg PRD (PPR − APR)</div>
								<div class="summary-value" :class="prdClass(summary.avg_prd)">{{ formatPr(summary.avg_prd) }}</div>
							</div>
						</template>
					</div>

					<p class="text-sm text-color-secondary mb-3">
						PR<sub>i</sub> = NEE<sub>act,i</sub> / (A<sub>i</sub>·GPOA<sub>i</sub>² + B<sub>i</sub>·GPOA<sub>i</sub> + C<sub>i</sub>).
						An hour is valid only with ≥10 samples and 100% good QF — no interpolation.
						Full export:
						<a class="cursor-pointer" @click="gotoReports()">Reports → Plant Performance Model</a>.
					</p>

					<!-- Hourly chart -->
					<PCard class="mb-4" v-if="chartSeries[0].data.length">
						<template #title>
							<span class="text-sm font-semibold">Hourly performance ratio</span>
						</template>
						<template #content>
							<apexchart
								type="line"
								height="280"
								:options="chartOptions"
								:series="chartSeries"
							/>
						</template>
					</PCard>

					<!-- Phase 7 W-C: temperature-corrected actual vs projected PR -->
					<PCard class="mb-4" v-if="hasTcMetrics && tcChartSeries[0].data.length">
						<template #title>
							<div class="flex align-items-center gap-2">
								<span class="text-sm font-semibold">Actual vs projected PR (temperature-corrected)</span>
								<PTag severity="info">Schedule 17 §3.12</PTag>
							</div>
						</template>
						<template #content>
							<apexchart
								type="line"
								height="280"
								:options="tcChartOptions"
								:series="tcChartSeries"
							/>
							<p class="text-sm text-color-secondary mt-2 mb-0">
								APR is the temperature-corrected actual PR; PPR is the contractual projected PR.
								Their gap (PRD = PPR − APR) drives the projected-energy deviation (EED).
							</p>
						</template>
					</PCard>

					<!-- Hourly table -->
					<h3 class="section-title">Hourly results</h3>
					<PDataTable
						:alwaysShowPaginator="false"
						:loading="loading"
						:rowHover="true"
						:rows="24"
						:showGridlines="true"
						:stripedRows="true"
						:value="hourly"
						data-key="hour_start"
						paginator
						selectionMode="single"
						size="small"
						v-model:selection="selectedHour"
						@row-select="onHourSelect"
					>
						<template #empty>No hourly PPM results yet. Run PPM for this period.</template>

						<PColumn field="hour_start" header="Hour start" headerClass="py-3" sortable />
						<PColumn field="value_avg" header="PR avg" headerClass="py-3" sortable>
							<template #body="{ data }">
								<span>{{ formatPr(data.value_avg) }}</span>
							</template>
						</PColumn>
						<PColumn v-if="hasTcMetrics" field="apr" header="APR" headerClass="py-3">
							<template #body="{ data }">
								<span>{{ formatPr(data.apr) }}</span>
							</template>
						</PColumn>
						<PColumn v-if="hasTcMetrics" field="ppr" header="PPR" headerClass="py-3">
							<template #body="{ data }">
								<span>{{ formatPr(data.ppr) }}</span>
							</template>
						</PColumn>
						<PColumn field="is_valid" header="Valid" headerClass="py-3" style="width: 100px;">
							<template #body="{ data }">
								<PTag :severity="data.is_valid ? 'success' : 'danger'">
									{{ data.is_valid ? 'valid' : 'invalid' }}
								</PTag>
							</template>
						</PColumn>
						<PColumn field="invalid_reason" header="Reason" headerClass="py-3">
							<template #body="{ data }">
								<span>{{ data.invalid_reason || '—' }}</span>
							</template>
						</PColumn>
						<PColumn field="sample_count" header="Samples" headerClass="py-3" sortable />
						<PColumn field="valid_sample_count" header="Valid samples" headerClass="py-3" sortable />
					</PDataTable>

					<!-- Minute samples -->
					<div class="flex align-items-center justify-content-between flex-wrap gap-2 mt-4 mb-2">
						<h3 class="section-title m-0">
							Minute samples
							<span v-if="sampleHourFilter" class="text-sm font-normal text-color-secondary">
								· hour {{ sampleHourFilter }}
							</span>
						</h3>
						<div class="flex align-items-center gap-2">
							<PButton
								v-if="sampleHourFilter"
								@click="clearHourFilter()"
								label="Show all hours"
								link
								size="small"
							/>
							<span class="text-sm text-color-secondary">
								{{ samples.total }} total
							</span>
						</div>
					</div>
					<PDataTable
						:alwaysShowPaginator="true"
						:lazy="true"
						:loading="samplesLoading"
						:rowHover="true"
						:rows="sampleRowsPerPage"
						:showGridlines="true"
						:stripedRows="true"
						:totalRecords="samples.total"
						:value="samples.rows"
						data-key="ts_minute"
						paginator
						size="small"
						@page="onSamplesPage($event)"
					>
						<template #empty>No minute samples for this filter.</template>

						<PColumn field="ts_minute" header="Minute" headerClass="py-3" sortable />
						<PColumn field="hour_start" header="Hour" headerClass="py-3" />
						<PColumn field="sample_value" header="PR sample" headerClass="py-3">
							<template #body="{ data }">
								<span>{{ formatPr(data.sample_value) }}</span>
							</template>
						</PColumn>
						<PColumn field="qf" header="QF" headerClass="py-3" style="width: 80px;">
							<template #body="{ data }">
								<PTag :severity="data.qf === 1 ? 'success' : 'danger'">
									{{ data.qf === 1 ? 'good' : 'bad' }}
								</PTag>
							</template>
						</PColumn>
					</PDataTable>
				</template>
			</template>
		</PCard>
	</div>
</template>

<script lang="ts">
	import { defineComponent } from 'vue'
	import store from '@/store'
	import Utils from '@/utils/Utils'
	import PpmBroker, { PpmResults, PpmHourlyRow, PpmSummary } from '@/brokers/PpmBroker'

	interface PpmPeriodOption {
		id: number
		name: string
		status: string
		is_trial?: boolean
	}

	interface PpmSamplesPage {
		total: number
		offset: number
		limit: number
		hour_start: string | null
		rows: Array<{
			ts_minute: string
			hour_start: string
			sample_value: number | null
			qf: number | null
		}>
	}

	interface PerformanceState {
		loading: boolean
		running: boolean
		samplesLoading: boolean
		billingPeriodId: number | null
		periods: PpmPeriodOption[]
		results: PpmResults | null
		selectedHour: PpmHourlyRow | null
		sampleHourFilter: string | null
		sampleOffset: number
		sampleRowsPerPage: number
		samples: PpmSamplesPage
	}

	const emptySummary = (): PpmSummary => ({
		sample_count: 0,
		valid_hours: 0,
		invalid_hours: 0,
		hour_count: 0,
		avg_pr: null,
	})

	export default defineComponent({
		name: 'PerformanceView',

		data(): PerformanceState {
			return {
				loading: false,
				running: false,
				samplesLoading: false,
				billingPeriodId: null,
				periods: [],
				results: null,
				selectedHour: null,
				sampleHourFilter: null,
				sampleOffset: 0,
				sampleRowsPerPage: 50,
				samples: {
					total: 0,
					offset: 0,
					limit: 50,
					hour_start: null,
					rows: [],
				},
			}
		},

		computed: {
			canRun(): boolean {
				return store.getters.can('ppm', 'run')
			},
			periodOptions(): Array<{ label: string; value: number }> {
				return this.periods.map((p) => ({
					label: `${p.name}${p.is_trial ? ' (trial)' : ''} · ${p.status}`,
					value: p.id,
				}))
			},
			summary(): PpmSummary {
				return this.results?.summary ?? emptySummary()
			},
			hourly(): PpmHourlyRow[] {
				return this.results?.hourly ?? []
			},
			chartSeries(): Array<{ name: string; data: Array<{ x: number; y: number }> }> {
				const hourly = this.results?.hourly ?? []
				const data = hourly
					.filter((h: PpmHourlyRow) => h.value_avg != null)
					.map((h: PpmHourlyRow) => ({
						x: new Date(h.hour_start.replace(' ', 'T')).getTime(),
						y: Number(h.value_avg),
					}))
				return [{ name: 'PR avg', data }]
			},
			chartOptions(): Record<string, unknown> {
				return {
					chart: {
						id: 'ppm-hourly',
						toolbar: { show: false },
						zoom: { enabled: true },
					},
					stroke: { curve: 'smooth', width: 2 },
					dataLabels: { enabled: false },
					xaxis: { type: 'datetime' },
					yaxis: {
						title: { text: 'Performance ratio' },
						decimalsInFloat: 3,
					},
					tooltip: { x: { format: 'dd MMM yyyy HH:mm' } },
					colors: ['#0d9488'],
				}
			},
			// Phase 7 W-C — temperature-corrected metrics availability.
			hasTcMetrics(): boolean {
				return Boolean(this.results?.summary?.tc_metrics_available)
			},
			tcChartSeries(): Array<{ name: string; data: Array<{ x: number; y: number }> }> {
				const hourly = this.results?.hourly ?? []
				const toPoints = (key: 'apr' | 'ppr') =>
					hourly
						.filter((h: PpmHourlyRow) => h[key] != null)
						.map((h: PpmHourlyRow) => ({
							x: new Date(h.hour_start.replace(' ', 'T')).getTime(),
							y: Number(h[key]),
						}))
				return [
					{ name: 'APR (actual)', data: toPoints('apr') },
					{ name: 'PPR (projected)', data: toPoints('ppr') },
				]
			},
			tcChartOptions(): Record<string, unknown> {
				return {
					chart: {
						id: 'ppm-tc-hourly',
						toolbar: { show: false },
						zoom: { enabled: true },
					},
					stroke: { curve: 'smooth', width: 2 },
					dataLabels: { enabled: false },
					legend: { position: 'top' },
					xaxis: { type: 'datetime' },
					yaxis: {
						title: { text: 'Performance ratio' },
						decimalsInFloat: 3,
					},
					tooltip: { x: { format: 'dd MMM yyyy HH:mm' } },
					colors: ['#0d9488', '#6366f1'],
				}
			},
		},

		async mounted() {
			await this.loadPeriods()
		},

		methods: {
			formatPr(value: number | null | undefined) {
				if (value == null || Number.isNaN(Number(value))) return '—'
				return Number(value).toFixed(4)
			},
			prdClass(value: number | null | undefined) {
				if (value == null || Number.isNaN(Number(value))) return ''
				// A positive PRD (projected exceeds actual) is an underperformance signal.
				return Number(value) > 0 ? 'text-orange-500' : 'text-green-500'
			},
			periodSeverity(status: string) {
				if (status === 'open') return 'info'
				if (status === 'finalized') return 'warning'
				if (status === 'invoiced') return 'success'
				return 'secondary'
			},
			async loadPeriods() {
				try {
					this.periods = await PpmBroker.getBillingPeriods()
				} catch (e: any) {
					Utils.showErrorMessage(e?.message || 'Could not load billing periods.')
				}
			},
			async onPeriodChange() {
				this.sampleHourFilter = null
				this.sampleOffset = 0
				this.selectedHour = null
				await this.refresh()
			},
			async refresh() {
				if (!this.billingPeriodId) return
				this.loading = true
				try {
					this.results = await PpmBroker.getResults({
						billing_period_id: this.billingPeriodId,
						hour_start: this.sampleHourFilter,
						limit: this.sampleRowsPerPage,
						offset: this.sampleOffset,
					})
					this.samples = this.results.samples
				} catch (e: any) {
					Utils.showErrorMessage(e?.message || 'Could not load PPM results.')
					this.results = null
				} finally {
					this.loading = false
				}
			},
			async loadSamplesOnly() {
				if (!this.billingPeriodId) return
				this.samplesLoading = true
				try {
					const res = await PpmBroker.getResults({
						billing_period_id: this.billingPeriodId,
						hour_start: this.sampleHourFilter,
						limit: this.sampleRowsPerPage,
						offset: this.sampleOffset,
					})
					this.samples = res.samples
					if (this.results) {
						this.results.samples = res.samples
					}
				} catch (e: any) {
					Utils.showErrorMessage(e?.message || 'Could not load minute samples.')
				} finally {
					this.samplesLoading = false
				}
			},
			async runPpm() {
				if (!this.billingPeriodId || !this.canRun) return
				this.running = true
				try {
					const payload = await PpmBroker.run(this.billingPeriodId)
					Utils.showSuccessMessage(
						`PPM complete: ${payload.sample_count} samples, `
						+ `${payload.valid_hours} valid / ${payload.invalid_hours} invalid hours.`
					)
					this.sampleOffset = 0
					await this.refresh()
				} catch (e: any) {
					Utils.showErrorMessage(e?.message || 'PPM run failed.')
				} finally {
					this.running = false
				}
			},
			async onHourSelect(event: any) {
				const hour = event?.data?.hour_start
				if (!hour) return
				this.sampleHourFilter = hour
				this.sampleOffset = 0
				await this.loadSamplesOnly()
			},
			async clearHourFilter() {
				this.sampleHourFilter = null
				this.selectedHour = null
				this.sampleOffset = 0
				await this.loadSamplesOnly()
			},
			async onSamplesPage(event: any) {
				this.sampleOffset = event.first ?? 0
				this.sampleRowsPerPage = event.rows ?? this.sampleRowsPerPage
				await this.loadSamplesOnly()
			},
			gotoReports() {
				this.$router.push({ name: 'reports' })
			},
		},
	})
</script>

<style scoped>
	.performance-empty {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 3rem 1rem;
		color: var(--text-color-secondary);
		gap: 0.75rem;
	}
	.performance-empty-icon {
		font-size: 2.5rem;
		opacity: 0.5;
	}
	.summary-grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
		gap: 0.75rem;
	}
	.summary-card {
		border: 1px solid var(--surface-border);
		border-radius: 6px;
		padding: 0.75rem 1rem;
		background: var(--surface-ground);
	}
	.summary-label {
		font-size: 0.75rem;
		color: var(--text-color-secondary);
		margin-bottom: 0.25rem;
	}
	.summary-value {
		font-size: 1.1rem;
		font-weight: 600;
	}
	.section-title {
		font-size: 0.95rem;
		font-weight: 600;
		margin: 0 0 0.5rem;
	}
</style>
